import { useEffect, useMemo } from 'react';

import { IEvent } from '../types';

import getEventMessage from '../utils/getEventMessage';
import formatDate from 'helpers/formatDate';
import {
  playListingEventTypes,
  playListingPriorityEventTypes,
  chartsEventTypes,
} from 'components/notifications/Settings/utils/createNewSubscription';
import { notFoundEntity } from '../../Settings/utils/filterNotFoundArtists';
import { PromiseState } from 'react-refetch';

export default useEvents;

function useEvents(
  events: undefined | IEventExtended[],
  countries: any[],
  regions: any[]
): undefined | INormalizedEvent[] {
  return useMemo(() => {
    const addCountryToEvents = addToEvents(countries, regions);

    if (!events) return undefined;

    return events
      .map(addEventMessage)
      .filter(filterEventsWithMessage)
      .map(addCountryToEvents)
      .map(formatDates);
  }, [events, countries, regions]);
}

function addEventMessage(event: IEventWithPlaylist): IEventWithMessage {
  const { eventType, entity, relatedEntity, values } = event;
  const { type, displayName } = entity;
  const formattedValues = values?.reduce(
    (prev: IObject, item: IObject) => ({
      ...prev,
      [`${item.fieldName}`]: item.value,
    }),
    {}
  );
  const detailData = {
    displayName,
    relatedEntity,
    values: formattedValues,
  };
  const eventMessage = getEventMessage(eventType)(
    type,
    displayName,
    detailData
  );

  return { ...event, eventMessage };
}

function filterEventsWithMessage(event: IEventWithMessage): boolean {
  return !!event.eventMessage;
}

function addToEvents(countries: any[], regions: any[]) {
  return function (event: IEventWithMessage): IEventWithCountry {
    const global = { id: 'WW', displayName: 'Global' };
    const country = countries.find(isSameId);
    const isRegion = event.countryId && event.countryId.startsWith('RE-');
    const region = regions.find((region: any) => region.id === event.countryId);

    function isSameId(country: any): boolean {
      return country.id === event.countryId;
    }

    return { ...event, country: isRegion ? region : country || global };
  };
}

function formatDates(event: IEventWithCountry): INormalizedEvent {
  const eventDate = new Date(event.eventTimestamp);
  const date = formatDate(eventDate, 'day', true, true, false);

  return { ...event, date };
}

interface IEventWithMessage extends IEventExtended {
  eventMessage: string | undefined;
}

interface IEventWithCountry extends IEventWithMessage {
  country: {
    displayName: string;
    id: string;
    flagImgSmall: string;
    flagImgMedium: string;
    flagImgLarge: string;
  };
}

export interface INormalizedEvent extends IEventWithCountry {
  date: string;
  eventTypeSubCategory?: 'All' | 'Priority';
}

interface IObject {
  [key: string]: string;
}

interface IEventWithPlaylist extends IEventExtended {
  values?: {
    fieldName: string;
    value: string;
  }[];
}

interface IEventExtended extends IEvent {
  eventTypeCategory: string;
}

export const useExtendedEventType = (
  notifications: IEvent[] | undefined
): IEventExtended[] | undefined => {
  return useMemo(() => {
    if (!notifications) return undefined;

    return notifications.map((item: IEvent) => {
      let eventTypeCategory = 'Performance';
      let eventTypeSubCategory = '';

      if (
        [...playListingEventTypes, ...playListingPriorityEventTypes].indexOf(
          item.eventType
        ) !== -1
      ) {
        eventTypeCategory = 'Playlist Activity';
        eventTypeSubCategory =
          item?.eventType === 'priority_playlist_add' ||
          item?.eventType === 'priority_playlist_removal'
            ? 'Priority'
            : 'All';
      } else if (chartsEventTypes.indexOf(item.eventType) !== -1) {
        eventTypeCategory = 'Charts';
      }

      return {
        ...item,
        eventTypeCategory,
        eventTypeSubCategory,
      };
    });
  }, [notifications]);
};

export const useAcknowledgeNotFoundArtistEvents = (
  groupedUnreadEvents: any,
  postArtistMetaDataResponse: PromiseState<any>,
  postArtistMetaDataResponseValue: any[],
  acknowledgeEntityEvents: (entityId: any) => void
): void => {
  return useEffect(() => {
    if (
      groupedUnreadEvents &&
      postArtistMetaDataResponse?.value &&
      postArtistMetaDataResponse.value.length !==
        postArtistMetaDataResponseValue.length
    ) {
      const notFoundIds = postArtistMetaDataResponse.value
        .filter(notFoundEntity)
        .map((item: any) => item.id);
      const listToRead = Object.keys(groupedUnreadEvents).filter((key: any) =>
        notFoundIds.includes(key)
      );

      listToRead.forEach((entityId) => {
        acknowledgeEntityEvents(entityId);
      });
    }
  }, [
    postArtistMetaDataResponseValue,
    postArtistMetaDataResponse,
    groupedUnreadEvents,
    acknowledgeEntityEvents,
  ]);
};
